import React, { useState } from "react";
import "../assets/scss/style.css";
import {
  IconButton,
  Typography,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Backdrop,
  Box,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import { getDateFormat, BackdropStyles, CircularProgressStyles, DialogStyles, ButtonStyles, useIconMenuStyles } from "../utils/general";
import { ACCOUNT_INITIALIZE } from "../store/actions";
import configData from "../config";
import axios from "axios";
import Logout from "@mui/icons-material/Logout";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useTheme } from '@mui/styles';
import MenuIcon from "@mui/icons-material/Menu";
import { useRef, useEffect } from "react";
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideocamIcon from '@mui/icons-material/Videocam';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import CampaignIcon from '@mui/icons-material/Campaign';
import UploadIcon from '@mui/icons-material/Upload';
import BlockableLink from '../components/BlockableLink';
import { SET_MENU } from "../store/actions";
import { useLocation } from 'react-router-dom';

function FixedMenuLayout() {
  const classes = useIconMenuStyles();
  const account = useSelector((state) => state.account);
  const { token, user, permissions, authMenu, refresh, project } = account;
  const dispatcher = useDispatch();
  const dispatch = useDispatch();

  const [projectExpand, setProjectExpand] = useState(false);
  const [editProjectDialog, setEditProjectDialog] = useState(false);
  const [projectUpdate, setProjectUpdate] = useState(project);
  const [dialogError, setDialogError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [menuExpand, setMenuExpand] = useState(false);
  const [filterErrorDialog, setFilterErrorDialog] = useState(false);

  const menuRef = useRef(null);

  const theme = useTheme();

  const location = useLocation();
  const path = location.pathname;

  const noMenuPages = ['/dashboard', '/user_profile_list', '/organization_profile_list'];

  const cleanPath = path.split('?')[0].replace(/\/$/, '');
  const isNoMenuPage = noMenuPages.some(route => cleanPath.startsWith(route));

  const leftDrawerOpened = useSelector((state) => state.customization.opened);

  /**
 * useEffect (close menu on outside click)
 *
 * @summary Closes the expanded menu when a click occurs outside the menu area in the FixedMenuLayout component.
 *
 * @description
 * Adds a mousedown event listener to the document when the menu is expanded. If a click occurs outside the menuRef element, sets the menuExpand state to false. Removes the event listener when the menu is collapsed or the component unmounts to prevent memory leaks.
 *
 * @param {boolean} menuExpand - Indicates whether the menu is currently expanded.
 *
 * @returns {void}
 *
 * @example
 * useEffect(() => {
 *   // close menu on outside click
 * }, [menuExpand]);
 */
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuExpand(false);
      }
    }

    if (menuExpand) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuExpand]);

  /**
 * handleClose
 *
 * @summary Closes the expanded project menu in the FixedMenuLayout component.
 *
 * @description
 * Sets the projectExpand state to false, collapsing the expanded project menu and hiding its details.
 *
 * @returns {void}
 *
 * @example
 * handleClose();
 */
  const handleClose = () => {
    setProjectExpand(false);
  };

  const icons = [
    { name: "DashBoard", icon: <DashboardIcon />, link: "/dashboard", id: 'dashboard' },
    { name: "Upload Assets", icon: <CloudUploadIcon />, link: "/upload_movie_asstes", id: 'upload_movie_assets' },
    { name: "Edit Scenes", icon: <PlaylistPlayIcon />, link: `/video_management_data?id=${project.id}&flag=false`, id: 'video_management_report' },
    { name: "Scene View", icon: <VisibilityIcon />, link: `/scene_view?id=${project.id}`, id: 'scene_view' },
    { name: "Validation Summary", icon: <SummarizeIcon />, link: `/data_validation_report?id=${project.id}&flag=false`, id: 'summary_report' },
    { name: "Progress Status", icon: <UploadIcon />, link: `/progress_status_list?id=${project.id}`, id: 'progress_status' },
    { name: "Reports", icon: <AssignmentIcon />,
      children: [
        { name: "Script Report", icon: <MenuBookIcon />, link: `/script_report?id=${project.id}`, id: 'script' },
        { name: "Continuity Report", icon: <CameraswitchIcon />, link: `/continuity_report?id=${project.id}`, id: 'continuity_report' },
        { name: "Log Report", icon: <AssignmentIcon />, link: `/log_report?id=${project.id}`, id: 'log_report' },
        { name: "Camera Report", icon: <VideocamIcon />, link: `/camera_report?id=${project.id}`, id: 'camera_report' },
        { name: "Sound Report", icon: <CampaignIcon />, link: `/sound_report?id=${project.id}`, id: 'sound_report' },
      ],
    },
    { name: "Logout", icon: <Logout />, link: "/logout", id: 'logout' },
  ];

  /**
 * isActive
 *
 * @summary Determines if a sidebar menu item or its child is active based on the current route path.
 *
 * @description
 * Checks if the current route path starts with the link of the provided icon or child menu item. Returns true if the menu item or its child matches the current path, otherwise false. Used to highlight the active menu item in the sidebar.
 *
 * @param {Object} icon - The menu item object containing link and optional children.
 * @param {Object|null} [child=null] - The child menu item object to check for active state.
 *
 * @returns {boolean} True if the menu item or child is active, otherwise false.
 *
 * @example
 * const active = isActive(icon);
 * const childActive = isActive(icon, child);
 */
  const isActive = (icon, child = null) => {
    if (child) {
      return path.startsWith(child.link.split("?")[0]);
    }
    if (icon.children) {
      return false;
    }
    return path.startsWith(icon.link.split("?")[0]);
  };


  /**
 * useEffect (expand menu on route change)
 *
 * @summary Expands the sidebar menu group if the current route matches a child menu item's link.
 *
 * @description
 * Watches the current route path. When the path changes, iterates through the sidebar icons array and checks if any icon has children whose link matches the current path. If a match is found, sets the menuExpand state to the corresponding icon's name, ensuring the correct menu group is expanded on navigation.
 *
 * @param {string} path - The current route path from the location object.
 *
 * @returns {void}
 *
 * @example
 * useEffect(() => {
 *   // expand menu on route change
 * }, [path]);
 */
  useEffect(() => {
    icons.forEach(icon => {
      if (icon.children && icon.children.some(c => path.startsWith(c.link.split("?")[0]))) {
        setMenuExpand(icon.name);
      }
    });
  }, [path]);

  /**
 * handleDialogClose
 *
 * @summary Closes all open dialogs and resets related state in the FixedMenuLayout component.
 *
 * @description
 * Resets dialog error, edit project dialog, project update state, and filter error dialog state to their default values, ensuring a clean state for subsequent dialog interactions.
 *
 * @returns {void}
 *
 * @example
 * handleDialogClose();
 */
  function handleDialogClose() {
    setDialogError(null);
    setEditProjectDialog(false);
    setProjectUpdate(project);
    setFilterErrorDialog(false)
  }

  /**
 * handleProjectSubmit
 *
 * @summary Submits the edited project details to the backend and updates the Redux account state.
 *
 * @description
 * Validates required project fields. Creates a FormData object with updated project details and optional banner file. Sends a PATCH request to update the project on the backend. On success, updates the Redux account state, displays a success message, and manages dialog transitions. Handles error messages and submission state for both validation and API errors.
 *
 * @returns {void}
 *
 * @example
 * handleProjectSubmit();
 */
  function handleProjectSubmit() {
    if (
      !projectUpdate.name ||
      !projectUpdate.type ||
      !projectUpdate.genre ||
      !projectUpdate.project_by ||
      !projectUpdate.script_writer_name
    ) {
      setDialogError(
        <span className="err_color">Please fill all the mandatory fields. indicated *</span>
      );
      return false;
    }

    const sendingData = {
      name: projectUpdate.name,
      type: projectUpdate.type,
      genre: projectUpdate.genre,
      project_by: projectUpdate.project_by,
      script_writer_name: projectUpdate.script_writer_name,
    };

    const banner_file = document.getElementById("banner_file");
    const projectForm = new FormData();
    Object.keys(sendingData).forEach((key) => {
      projectForm.append(key, sendingData[key]);
    });

    if (banner_file && banner_file.value !== "") {
      const file = banner_file.files[0];
      projectForm.append("banner_file", file);
    }

    setSubmitted(true);
    axios
      .patch(
        configData.API_SERVER + "projects/" + project.id + "/",
        projectForm
      )
      .then((response) => {
        setSubmitted(true);
        dispatcher({
          type: ACCOUNT_INITIALIZE,
          payload: {
            isLoggedIn: true,
            user: user,
            token: token,
            permissions: permissions,
            authMenu: authMenu,
            refresh: refresh,
            project: projectUpdate,
          },
        });
        if (response.data) {
          setDialogError(
            <span className="success_color">Data submitted successfully</span>
          );
        } else {
          setDialogError(
            <span className="err_color">Data submission failed</span>
          );
          return false;
        }
        setTimeout(() => {
          setRedirect(true);
          setEditProjectDialog(false);
          setSubmitted(false);
          setTimeout(() => {
            setRedirect(false);
            setDialogError(null);
          }, 1000);
        }, 3000);
      })
      .catch((error) => {
        setSubmitted(false);
        setDialogError(
          <span className="err_color">{error.response.data.error}</span>
        );
      });
  }

  return (
    <div>
      <Grid
        item
        xs={12}
        container
        justifyContent="space-between"
        alignItems="center"
        backgroundColor={theme.palette.themeMode == 'dark' ? "#1D2029" : theme.palette.primary.dark}
        sx={{ px: 2 }}
      >
        <Grid item sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <BlockableLink to={`/dashboard`}>
            <span style={{ color: theme.palette.themeMode === 'dark' ? "#fff" : "#0A0A0A", fontSize: "22px" }} width="90" height="40">{configData.TITLE_TXT}</span>
          </BlockableLink>
          <IconButton onClick={() => dispatch({ type: SET_MENU, opened: !leftDrawerOpened })} size="large">
            <MenuIcon sx={{ color: theme.palette.themeMode === 'dark' ? theme.palette.primary.dark : "#0A0A0A" }} />
          </IconButton>

        </Grid>
        <Typography
          variant="h1"
          align="center"
          color={theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A"}
          marginTop={1}
          sx={{ flexGrow: 1, "@media (max-width: 768px)": { fontSize: "15px" } }}
          marginRight={15}
        >
          {project.name}
          <IconButton
            onClick={() => setProjectExpand(!projectExpand)}
            color={theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A"}
          >
            {!projectExpand ? <ExpandMoreIcon sx={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }} /> : <ExpandLessIcon sx={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }} />}
          </IconButton>
        </Typography>
      </Grid>
      {!isNoMenuPage && leftDrawerOpened && (
        <div
          style={{
            position: "fixed",
            top: "58px",
            left: 0,
            width: "220px",
            height: "100vh",
            backgroundColor: theme.palette.themeMode === "dark"
              ? "#1D2029"
              : "#fff",
            overflowY: "auto",
            transition: "all 0.3s ease",
            zIndex: 1000,
          }}
        >
          <Grid item xs={12}>
            {icons.map((icon, index) =>
              icon.children ? (
                <div key={index}>
                  <div
                    onClick={() => setMenuExpand(menuExpand === icon.name ? null : icon.name)}
                    className={classes.iconItem}
                    style={{ cursor: "pointer", backgroundColor: "transparent" }}
                  >
                    <div className="text-color">{icon.icon}</div>
                    <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.themeMode === 'dark' ? "#fff" : "#0A0A0A" }}>
                      {icon.name}
                    </Typography>
                    {menuExpand === icon.name ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>

                  {menuExpand === icon.name && (
                    <div style={{ marginLeft: "20px" }}>
                      {icon.children.map((child, idx) => (
                        <BlockableLink
                          to={child.link}
                          key={idx}
                          state={project}
                          className={classes.iconItem}
                          style={{
                            backgroundColor: isActive(icon, child)
                              ? (theme.palette.themeMode === 'dark' ? "#91887e" : "#87cefa")
                              : "transparent"
                          }}
                        >
                          <div className="text-color">{child.icon}</div>
                          <Typography variant="caption" sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.themeMode === 'dark' ? "#fff" : "#0A0A0A" }}>
                            {child.name}
                          </Typography>
                        </BlockableLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <BlockableLink
                  to={icon.link}
                  key={index}
                  state={project}
                  className={classes.iconItem}
                  style={{
                    backgroundColor: isActive(icon)
                      ? (theme.palette.themeMode === 'dark' ? "#91887e" : "#87cefa")
                      : "transparent"
                  }}
                >
                  <div className="text-color">{icon.icon}</div>
                  <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.themeMode === 'dark' ? "#fff" : "#0A0A0A" }}>
                    {icon.name}
                  </Typography>
                </BlockableLink>
              )
            )}
          </Grid>
        </div>
      )}
      {projectExpand && (<>
        <Box
          onClick={handleClose}
          sx={{
            position: "fixed",
            top: 0,
            left: leftDrawerOpened ? "220px" : 0,
            width: leftDrawerOpened ? "calc(100% - 220px)" : "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(2px)",
            zIndex: 998,
          }}
        />
        <Grid
          item
          xs={12}
          container
          spacing={1}
          marginTop={10}
          style={{
            position: "fixed",
            top: "10%",
            left: "60%",
            transform: "translate(-50%, -50%)",
            backgroundColor: theme.palette.themeMode == 'dark' ? "#2C2B4C" : theme.palette.primary.dark,
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.8)",
            zIndex: 1500,
            color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A",
            width: leftDrawerOpened ? "calc(100% - 240px)" : "80%",
            maxWidth: "900px"
          }}
        >
          <Grid item xs={12}>
            <IconButton
              sx={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setEditProjectDialog(true)}
            >
              <EditIcon style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }} />
            </IconButton>
          </Grid>
          <Grid item container xs={12} sm={6}>
            <Grid item xs={6}>
              <label className="form-label0" style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>Project Name</label>
            </Grid>
            <Grid item xs={0.3} style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              :
            </Grid>
            <Grid item xs={5} style={{ wordWrap: "break-word", color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              {project.name}
            </Grid>
          </Grid>
          <Grid item container xs={12} sm={6}>
            <Grid item xs={6}>
              <label className="form-label0" style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>Date</label>
            </Grid>
            <Grid item xs={0.3} style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              :
            </Grid>
            <Grid item xs={5} style={{ wordWrap: "break-word", color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              {getDateFormat(project.date)}
            </Grid>
          </Grid>
          <Grid item container xs={12} sm={6}>
            <Grid item xs={6}>
              <label className="form-label0" style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>Project Type</label>
            </Grid>
            <Grid item xs={0.3} style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              :
            </Grid>
            <Grid item xs={5} style={{ wordWrap: "break-word", color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              {project.type}
            </Grid>
          </Grid>
          <Grid item container xs={12} sm={6}>
            <Grid item xs={6}>
              <label className="form-label0" style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>Project Genre</label>
            </Grid>
            <Grid item xs={0.3} style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              :
            </Grid>
            <Grid item xs={5} style={{ wordWrap: "break-word", color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              {project.genre}
            </Grid>
          </Grid>
          <Grid item container xs={12} sm={6}>
            <Grid item xs={6}>
              <label className="form-label0" style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>Project By</label>
            </Grid>
            <Grid item xs={0.3} style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "0A0A0A" }}>
              :
            </Grid>
            <Grid item xs={5} style={{ wordWrap: "break-word", color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              {project.project_by}
            </Grid>
          </Grid>
          <Grid item container xs={12} sm={6}>
            <Grid item xs={6}>
              <label className="form-label0" style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>Script Writer Name</label>
            </Grid>
            <Grid item xs={0.3} style={{ color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              :
            </Grid>
            <Grid item xs={5} style={{ wordWrap: "break-word", color: theme.palette.themeMode == 'dark' ? theme.palette.primary.dark : "#0A0A0A" }}>
              {project.script_writer_name}
            </Grid>
          </Grid>
        </Grid>
      </>)}

      <Dialog
        open={editProjectDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleDialogClose();
          }
        }}
        sx={DialogStyles(theme)}
      >
        <DialogContent>
          <span className="custom-title">
            Edit the project details
            <br />
          </span>
          <TextField
            autoFocus
            margin="dense"
            id="project_name"
            label={
              <span style={{ fontSize: "13px", color: theme.palette.themeMode == "dark" ? "#fff" : "#000" }}>
                Project Name <span className="err_color">*</span>
              </span>
            }
            type="text"
            variant="standard"
            fullWidth
            value={projectUpdate && projectUpdate.name}
            onChange={(e) =>
              setProjectUpdate({ ...projectUpdate, name: e.target.value })
            }
            className="custom-input"
          />

          <br />
          <TextField
            margin="dense"
            id="project_type"
            label={
              <span style={{ fontSize: "13px", color: theme.palette.themeMode == "dark" ? "#fff" : "#000" }}>
                Project Type <span className="err_color">*</span>
              </span>
            }
            type="text"
            variant="standard"
            fullWidth
            value={projectUpdate && projectUpdate.type}
            onChange={(e) =>
              setProjectUpdate({ ...projectUpdate, type: e.target.value })
            }
            className="custom-input"
          />
          <br />
          <TextField
            margin="dense"
            id="project_genre"
            label={
              <span style={{ fontSize: "13px", color: theme.palette.themeMode == "dark" ? "#fff" : "#000" }}>
                Project Genre <span className="err_color">*</span>
              </span>
            }
            type="text"
            variant="standard"
            fullWidth
            value={projectUpdate && projectUpdate.genre}
            onChange={(e) =>
              setProjectUpdate({ ...projectUpdate, genre: e.target.value })
            }
            className="custom-input"
          />
          <br />
          <TextField
            margin="dense"
            id="project_by"
            label={
              <span style={{ fontSize: "13px", color: theme.palette.themeMode == "dark" ? "#fff" : "#000" }}>
                Project By <span className="err_color">*</span>
              </span>
            }
            type="text"
            variant="standard"
            fullWidth
            value={projectUpdate && projectUpdate.project_by}
            onChange={(e) =>
              setProjectUpdate({ ...projectUpdate, project_by: e.target.value })
            }
            className="custom-input"
          />
          <br />
          <TextField
            margin="dense"
            id="script_writer_name"
            label={
              <span style={{ fontSize: "13px", color: theme.palette.themeMode == "dark" ? "#fff" : "#000" }}>
                Script Wrtter Name <span className="err_color">*</span>
              </span>
            }
            type="text"
            variant="standard"
            fullWidth
            value={projectUpdate && projectUpdate.script_writer_name}
            onChange={(e) =>
              setProjectUpdate({
                ...projectUpdate,
                script_writer_name: e.target.value,
              })
            }
            className="custom-input"
          />
          <br />
          <label htmlFor="banner_file" style={{ fontSize: "10px", color: theme.palette.themeMode == "dark" ? "#fff" : "#000" }}>Banner File</label>
          <TextField
            margin="dense"
            id="banner_file"
            type="file"
            variant="standard"
            fullWidth
            inputProps={{ accept: "image/*" }}
            className="custom-input"
          />
          <br />
        </DialogContent>
        <DialogActions>
          <Grid item container xs={12}>
            <Grid item xs={12}>
              <div className="error" style={{ width: "100%" }}>
                {dialogError}
              </div>
            </Grid>
            <Grid
              item
              container
              spacing={2}
              sx={{ display: "flex", justifyContent: "right" }}
            >
              <Grid item>
                <Button style={ButtonStyles(theme)} size="small" onClick={handleDialogClose}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                {submitted ? (
                  redirect ? null : (
                    <>
                      <Button style={ButtonStyles(theme)} size="small" disabled>
                        Submit
                      </Button>
                      <Backdrop open={true} sx={BackdropStyles()}>
                        <CircularProgress sx={{ ...CircularProgressStyles() }} />
                      </Backdrop>
                    </>
                  )
                ) : (
                  <Button style={ButtonStyles(theme)} size="small" onClick={handleProjectSubmit}>
                    Submit
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <Dialog
        open={filterErrorDialog}
        // onClose={handleDialogClose}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleDialogClose();
          }
        }}
        sx={DialogStyles(theme)}
      >
        <DialogContent>
          <Typography style={{ fontWeight: "bold", color: "red" }}>
            Failed to Change Filter, Try Again
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button style={ButtonStyles(theme)} size="small" onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FixedMenuLayout;
